import type { DashboardsWidget } from '@smarthome/api';

/**
 * Grid constants for the 12-column layout
 */
export const GRID_COLUMNS = 12;
export const ROW_HEIGHT_PX = 80; // Base row height in pixels
export const GRID_GAP_PX = 16; // Gap between grid cells (matches gap-4 / 1rem)

/**
 * Breakpoints for responsive behavior
 */
export const BREAKPOINTS = {
  mobile: 640, // Below this: single column stack
  desktop: 1024, // Above this: full 12-column grid
} as const;

/**
 * Sort widgets by position for consistent rendering order.
 * Primary sort: y (top to bottom)
 * Secondary sort: x (left to right)
 */
export function sortWidgetsByPosition(widgets: DashboardsWidget[]): DashboardsWidget[] {
  return [...widgets].sort((a, b) => {
    const yA = a.y ?? 0;
    const yB = b.y ?? 0;
    const xA = a.x ?? 0;
    const xB = b.x ?? 0;

    if (yA !== yB) {
      return yA - yB;
    }
    return xA - xB;
  });
}

/**
 * Compute CSS grid placement styles for a widget
 */
export function getGridPlacement(widget: DashboardsWidget): React.CSSProperties {
  const x = widget.x ?? 0;
  const y = widget.y ?? 0;
  const w = widget.w ?? 2;
  const h = widget.h ?? 1;

  return {
    gridColumnStart: x + 1,
    gridColumnEnd: `span ${Math.min(w, GRID_COLUMNS)}`,
    gridRowStart: y + 1,
    gridRowEnd: `span ${h}`,
  };
}

/**
 * Compute minimum height for a widget based on its grid height.
 * Includes internal row gaps to match desktop grid height.
 */
export function getMinHeight(widget: DashboardsWidget): number {
  const h = widget.h ?? 1;
  // Height = h rows × row_height + (h-1) gaps between rows
  return h * ROW_HEIGHT_PX + Math.max(0, h - 1) * GRID_GAP_PX;
}

/**
 * Validate widget has required position data
 */
export function hasValidPosition(widget: DashboardsWidget): boolean {
  return (
    typeof widget.x === 'number' &&
    typeof widget.y === 'number' &&
    typeof widget.w === 'number' &&
    typeof widget.h === 'number'
  );
}

/**
 * Clamp widget width to grid bounds
 */
export function clampWidgetWidth(w: number, x: number): number {
  const maxWidth = GRID_COLUMNS - x;
  return Math.min(Math.max(1, w), maxWidth);
}
